// useWalkthrough.ts
import { useState, useEffect, ReactNode, useRef } from "react";

/* ------------------------------ Localization ------------------------------ */

export type Language = "en" | "tl";

// Wrap any text field that should support translation. Authors can pass a
// plain value (used for both languages) or a `{ en, tl }` object. The
// resolver below falls back to the plain value when no translation exists.
export type Localized<T> = T | { en: T; tl: T };

const isLocalizedShape = <T,>(value: unknown): value is { en: T; tl: T } => {
  if (!value || typeof value !== "object") return false;
  if (Array.isArray(value)) return false;
  // React elements are objects too — skip them. They expose $$typeof.
  if ((value as { $$typeof?: unknown }).$$typeof !== undefined) return false;
  const keys = Object.keys(value as object);
  return keys.length === 2 && keys.includes("en") && keys.includes("tl");
};

export const resolveLocalized = <T,>(
  value: Localized<T>,
  lang: Language,
): T => {
  if (isLocalizedShape<T>(value)) {
    return value[lang];
  }
  return value as T;
};

export const WALKTHROUGH_LANGUAGE_STORAGE_KEY = "walkthrough:language";

export interface WalkthroughPersistenceOptions {
  // localStorage key under which the current step index is persisted. When set,
  // the walkthrough resumes from the saved step on next startWalkthrough().
  // Omit to disable persistence (default).
  storageKey?: string;
  // Bump when the step list changes incompatibly (reorder / insert / remove).
  // Saved entries with a mismatching version are discarded so users don't land
  // on the wrong step. Defaults to 1.
  storageVersion?: number;
}

const readSavedStepIndex = (
  storageKey: string | undefined,
  storageVersion: number,
  stepsLength: number,
): number | null => {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: number; i?: number };
    if (parsed.v !== storageVersion) return null;
    if (typeof parsed.i !== "number" || parsed.i < 0 || parsed.i >= stepsLength) return null;
    return parsed.i;
  } catch {
    return null;
  }
};

export type CaptionPosition =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "center-left"
  | "near-left" ;

export interface HighlightItem {
  // The element to highlight. Provide `id` (resolved via getElementById) for
  // elements you control. Use `selector` (resolved via querySelector) to target
  // elements without a stable id — e.g. a third-party widget reachable only by
  // class (`#chatbot-widget-root .cw-fab`). `selector` takes precedence if both
  // are set. At least one is required.
  id?: string;
  selector?: string;
}
// Ties a spoken phrase to a form field element. When Read Aloud reaches the
// sentence that contains `phrase`, the matched element blinks until the next
// sentence starts. Supports both id and CSS selector targeting.
export interface FieldHighlightItem {
  phrase: string;
  id?: string;
  selector?: string;
}
// Text fields accept ReactNode so consumers can pass JSX. Plain strings still
// work — the renderer additionally parses **bold** segments in any string value.
// Wrap with Localized<…> for translatable text: { en: "Foo", tl: "Bar" }.
export interface SuperMiniTitle {
  superMiniTitle: Localized<ReactNode>;
}
export interface MiniTitle {
  miniTitle: Localized<ReactNode>;
  superMiniTitle?:  SuperMiniTitle[];
}
export interface SubTitle {
  subTitle: Localized<ReactNode>;
  miniTitle?:  MiniTitle[];
}
export interface SubCaptionTitle {
  title: Localized<ReactNode>;
  subTitle: SubTitle[]
}

export interface WalkthroughStep {
  showTableData?: boolean;
  title: Localized<ReactNode>;
  caption: Localized<ReactNode>; // The text to display in the modal for the step
  subCaption?: SubCaptionTitle[];
  highlights: HighlightItem[]; // An array of elements to highlight in this step
  // When Read Aloud is active, blink the listed field elements while the
  // sentence containing their phrase is being spoken.
  fieldHighlights?: FieldHighlightItem[];
  captionPosition?: CaptionPosition; // Optional position for the caption modal
  delayBeforeNext?: number;
  delayAfterNext?: number;
  isButtonOrSearchBar?: boolean;
  showScrollbar?: boolean;
  clicksOfNext?: number;
  adjustMarginTop?: number;
  // Optional sub-flow label. Steps sharing the same group form a paged section.
  group?: Localized<string>;
  // When true, the walkthrough will never auto-advance past this step even if
  // the highlighted element isn't found immediately. Use on steps that open a
  // dialog or modal whose render is deferred (Portal + animation).
  skipAutoAdvance?: boolean;
  // When true, auto-advance fires after ~80ms instead of 800ms. Use on steps
  // whose target is either in the DOM immediately or not at all (no lazy load).
  fastSkip?: boolean;
}

export interface GroupInfo {
  // Raw localized value — render-side resolves to the active language.
  name: Localized<string>;
  indexInGroup: number; // 1-based for display
  groupSize: number;
}

export const useWalkthrough = (
  steps: WalkthroughStep[],
  onNext?: (step: WalkthroughStep, index: number) => void | Promise<void>,
  onStart?: (step: WalkthroughStep, index: number) => void | Promise<void>,
  persistence?: WalkthroughPersistenceOptions,
) => {
  const storageKey = persistence?.storageKey;
  const storageVersion = persistence?.storageVersion ?? 1;

  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [ isActive, setIsActive ] = useState(false);
  const [ numberOfClicks, setNumberOfClicks ] = useState(0);

  // Set to true for the duration of a backward navigation so walkthrough.tsx's
  // "element not found → auto-advance" guard doesn't fire while the closing
  // modal/menu is still being committed to the DOM.
  const isBackwardNavigating = useRef(false);

  // Re-entrancy guard for nextStep/previousStep. A page's onNext transition hook
  // commonly closes a menu/modal synchronously, and some of those menus call
  // nextStep() from their own onClose. That second call would re-run onNext with
  // the SAME (not-yet-advanced) index, close the menu again, fire onClose again,
  // and recurse until "Maximum call stack size exceeded". While a transition is
  // in flight we ignore overlapping calls.
  const isTransitioning = useRef(false);

  // Live mirror of `isActive` that updates synchronously (state is async). The
  // teardown paths ("Skip for now" / "Don't show again" / quiz) call onCloseAll
  // BEFORE endWalkthrough, and onCloseAll closes menus whose onClose may call
  // nextStep() while the stale `isActive` closure still reads true. That
  // nextStep is async: it awaits onNext, by which point endWalkthrough has set
  // the index to -1 — then it would resume and setCurrentStepIndex(captured + 1),
  // re-activating the tour the user just dismissed. nextStep/previousStep
  // re-read this ref AFTER their awaits and bail if the tour was torn down
  // mid-transition.
  const isActiveRef = useRef(false);

  // Persist the active step so a "Skip for now" / page leave resumes here on
  // next start. We only write while the walkthrough is active (index >= 0)
  // AND not on the final step — the last step has no "resume to" target
  // (only Finish), so saving it would make a page-refresh resume at N/N,
  // which looks broken. Tearing down to -1 should not clobber saved progress.
  useEffect(() => {
    if (!storageKey || currentStepIndex < 0) return;
    if (currentStepIndex >= steps.length - 1) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
      return;
    }
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ v: storageVersion, i: currentStepIndex }),
      );
    } catch {
      // localStorage may be disabled / full — silently skip.
    }
  }, [storageKey, storageVersion, currentStepIndex, steps.length]);

  const clearSavedProgress = () => {
    if (!storageKey) return;
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  };

  const startWalkthrough = async () => {
    const saved = readSavedStepIndex(storageKey, storageVersion, steps.length);
    // Resume mid-tour: replay the transition hook for (saved - 1) so the page
    // navigates/opens whatever the saved step expects, THEN jump the index.
    //
    // IMPORTANT: onNext must run (and its DOM side-effects must settle) BEFORE
    // setCurrentStepIndex fires. Otherwise walkthrough.tsx's effect runs first,
    // finds zero rects for the saved step (the route/modal it depends on isn't
    // ready yet), and auto-advances — exactly the bug previousStep already
    // guards against with isBackwardNavigating. Resume needs the same shape.
    if (saved !== null && saved > 0) {
      setIsActive(true);
      isActiveRef.current = true;
      isBackwardNavigating.current = true;
      try {
        if (onNext) {
          const replayIndex = saved - 1;
          const replayStep = replayIndex >= 0 ? steps[replayIndex] : steps[0];
          await onNext(replayStep, replayIndex);
          // Honour the same post-transition delay forward/backward navigation
          // uses — gives portals/routes time to commit before the step updates.
          const delay = replayIndex >= 0 ? steps[replayIndex]?.delayAfterNext : undefined;
          if (delay) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
        setCurrentStepIndex(saved);
      } finally {
        isBackwardNavigating.current = false;
      }
      return;
    }
    const firstStep = steps[0];
    setCurrentStepIndex(0);
    setIsActive(true);
    isActiveRef.current = true;
    if (onStart) {
      onStart(firstStep, 0);
    }
  };

  const nextStep = async () => {
    // Ignore overlapping calls — a menu closed by this very transition must not
    // re-enter nextStep (see isTransitioning).
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    try {
      var current = steps[currentStepIndex];
      if (current.delayBeforeNext) {
        await new Promise((resolve) =>
          setTimeout(resolve, current.delayBeforeNext)
        );
      }

      if (onNext && currentStepIndex >= 0) {
        await onNext(current, currentStepIndex);
      }

      if (current.delayAfterNext) {
        await new Promise((resolve) =>
          setTimeout(resolve, current.delayAfterNext)
        );
      }

      // The tour was torn down while we awaited above (e.g. "Skip for now"
      // closed a menu whose onClose called us). Don't resurrect it by writing
      // a new step index.
      if (!isActiveRef.current) return;

      if (currentStepIndex < steps.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      } else {
        // Reached the final step — completed normally, drop saved progress so a
        // future start opens at step 0 instead of resuming on the last step.
        endWalkthrough({ clearProgress: true });
      }
    } finally {
      isTransitioning.current = false;
    }
  };

  // Move backward by one step. The page-side onNext callback is a TRANSITION
  // hook: when called with index N it prepares the UI for landing on N+1
  // (e.g. opens the modal step N+1 needs). So to land on `target` we replay
  // onNext for target - 1.
  //
  // When target === 0 we replay with index = -1: a sentinel "before-start"
  // value. Transition functions follow an `if (index === N) open / if (index
  // !== N) close` pattern, so -1 skips every open path and triggers every
  // close path — i.e. it resets the UI to its initial state.
  //
  // IMPORTANT: onNext must run (and its DOM side-effects must settle) BEFORE
  // setCurrentStepIndex fires. If the step index changes first, walkthrough.tsx
  // runs its useEffect immediately, finds zero highlight rects (the menu/modal
  // the target step depends on isn't open yet), and auto-calls onNext() — which
  // bounces the user back forward. We honour the same delayAfterNext the
  // forward path uses so menus/portals are in the DOM before the step updates.
  const previousStep = async () => {
    if (currentStepIndex <= 0) return;
    // Same re-entrancy guard as nextStep: the replayed onNext below closes
    // menus/modals, whose onClose may call nextStep() — which must not run
    // mid-transition.
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    const targetIndex = currentStepIndex - 1;
    // Block the "element not found → auto-advance" guard in walkthrough.tsx for
    // the entire duration of the backward transition. Without this, closing a
    // modal/menu (to set up the previous step) makes the current highlight
    // element disappear from the DOM, the guard fires onNext() after 100 ms,
    // and the tour races forward instead of backward.
    isBackwardNavigating.current = true;
    try {
      if (onNext) {
        const replayIndex = targetIndex - 1;
        const replayStep = replayIndex >= 0 ? steps[replayIndex] : steps[0];
        await onNext(replayStep, replayIndex);
        // Honour the same post-transition delay the forward path uses when
        // advancing into this target step (e.g. 300 ms on the step that opens
        // the Manage Transmittal menu). This gives React time to commit the
        // portal/menu to the DOM before the Walkthrough useEffect re-runs.
        const delay = replayIndex >= 0 ? steps[replayIndex]?.delayAfterNext : undefined;
        if (delay) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
      // Same teardown-during-await guard as nextStep.
      if (!isActiveRef.current) return;
      setCurrentStepIndex(targetIndex);
    } finally {
      isBackwardNavigating.current = false;
      isTransitioning.current = false;
    }
  };

  // Tear down the walkthrough UI. By default the saved step is KEPT so a
  // "Skip for now" dismiss resumes at the same place next visit. Pass
  // `{ clearProgress: true }` from "Don't show again" / final-step completion
  // paths to wipe the saved index.
  const endWalkthrough = (options?: { clearProgress?: boolean }) => {
    isActiveRef.current = false;
    setCurrentStepIndex(-1);
    setIsActive(false);
    if (options?.clearProgress) {
      clearSavedProgress();
    }
  };

  const isWalkthroughActive = currentStepIndex !== -1;
  const currentStep = isWalkthroughActive ? steps[currentStepIndex] : null;

  // Compute group info for the active step: which sub-flow it belongs to,
  // its 1-based position within that sub-flow, and the sub-flow size.
  // Group values can be plain strings OR { en, tl } objects, so identity
  // comparison won't work — bucket by a canonical key (English form for
  // localized values; the string itself otherwise).
  const groupKey = (group: Localized<string> | undefined): string | undefined => {
    if (!group) return undefined;
    return typeof group === "string" ? group : group.en;
  };
  const groupInfo: GroupInfo | null = (() => {
    if (!isWalkthroughActive || !currentStep?.group) return null;
    const currentKey = groupKey(currentStep.group);
    const sameGroup = steps
      .map((s, i) => ({ step: s, i }))
      .filter(({ step }) => groupKey(step.group) === currentKey);
    const position = sameGroup.findIndex(({ i }) => i === currentStepIndex);
    return {
      name: currentStep.group,
      indexInGroup: position + 1,
      groupSize: sameGroup.length,
    };
  })();

  // Jump to any step via the slider.  Like previousStep, the onNext callback is
  // a TRANSITION hook: calling it with index N prepares the UI for landing on
  // N+1.  So to land on `targetIndex` we replay onNext for targetIndex-1 first,
  // then commit the index — the same pattern used by previousStep and the
  // startWalkthrough resume path.  isBackwardNavigating is set for the duration
  // so walkthrough.tsx's "element not found → auto-advance" guard doesn't fire
  // while the transition closes/opens modals (the highlight element temporarily
  // leaves the DOM).
  const goToStep = async (targetIndex: number) => {
    if (!isActiveRef.current) return;
    const clamped = Math.max(0, Math.min(targetIndex, steps.length - 1));
    if (clamped === currentStepIndex) return;
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    isBackwardNavigating.current = true;
    try {
      if (onNext) {
        if (clamped > currentStepIndex) {
          // Forward jump: replay ALL transitions from the current step through
          // target-1.  This ensures any "close dialog" transition between
          // current and target actually fires (e.g. case 6 in the Clients
          // walkthrough closes the Invite Client modal — replaying only
          // target-1 would skip it entirely).
          for (let i = currentStepIndex; i < clamped; i++) {
            await onNext(steps[i], i);
          }
          // Honour delayAfterNext only for the final transition so any portal
          // the target step depends on is in the DOM before the index commits.
          const lastDelay = steps[clamped - 1]?.delayAfterNext;
          if (lastDelay) {
            await new Promise((resolve) => setTimeout(resolve, lastDelay));
          }
        } else {
          // Backward jump: replay just target-1 (same single-step pattern as
          // previousStep).  The transition callbacks are written to fully reset
          // UI state when called with their specific index, so one replay is
          // sufficient to set up the target step correctly.
          const replayIndex = clamped - 1;
          const replayStep = replayIndex >= 0 ? steps[replayIndex] : steps[0];
          await onNext(replayStep, replayIndex);
          const delay = replayIndex >= 0 ? steps[replayIndex]?.delayAfterNext : undefined;
          if (delay) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }
      if (!isActiveRef.current) return;
      setCurrentStepIndex(clamped);
    } finally {
      isBackwardNavigating.current = false;
      isTransitioning.current = false;
    }
  };

  return {
    isWalkthroughActive,
    currentStep,
    currentStepIndex,
    totalSteps: steps.length,
    groupInfo,
    isActive,
    startWalkthrough,
    nextStep,
    previousStep,
    goToStep,
    endWalkthrough,
    numberOfClicks,
    setNumberOfClicks,
    isBackwardNavigating,
  };
};
